import moment from "moment";
import { FC, useRef, useState } from "react";
import {
  GoogleMap,
  useJsApiLoader,
  Polyline,
  Marker,
  LoadScriptProps,
} from "@react-google-maps/api";
import { getDriverTrackApi } from "../../api/user";
import { ITrackResponse } from "../../interface/track";
import { isValidTrackRange } from "../../utils/geo";
import useSnackbar from "../../utils/snackbar";
import { GOOGLE_MAPS_KEY } from "../../config";

const libraries: LoadScriptProps["libraries"] = ["geometry"];

interface DriverTrackAuditProps {
  driverId: string;
}

const DriverTrackAudit: FC<DriverTrackAuditProps> = ({ driverId }) => {
  let libRef = useRef(libraries);

  const { isLoaded, loadError } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: GOOGLE_MAPS_KEY,
    libraries: libRef.current,
  });

  const [openSnackbar] = useSnackbar({ position: "bottom-left" });
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [rangeError, setRangeError] = useState<string | null>(null);
  const [track, setTrack] = useState<ITrackResponse | null>(null);
  const [filter, setFilter] = useState({
    from: moment().subtract(1, "hours").format("yyyy-MM-DDTHH:mm"),
    to: moment().format("yyyy-MM-DDTHH:mm"),
  });

  const searchTrack = async () => {
    setRangeError(null);

    const fromSec = moment(filter.from).unix();
    const toSec = moment(filter.to).unix();

    if (!Number.isFinite(fromSec) || !Number.isFinite(toSec)) {
      setTrack(null);
      setSearched(true);
      setRangeError("กรุณาเลือกช่วงเวลา");
      return;
    }

    const { valid, reason } = isValidTrackRange(fromSec, toSec);
    if (!valid) {
      setTrack(null);
      setSearched(true);
      if (reason === "order") {
        setRangeError("ช่วงเวลาไม่ถูกต้อง (สิ้นสุดก่อนเริ่มต้น)");
      } else if (reason === "tooLarge") {
        setRangeError("เลือกช่วงเวลาได้ไม่เกิน 24 ชั่วโมง");
      }
      return;
    }

    try {
      setLoading(true);
      const { data } = await getDriverTrackApi(driverId, {
        from: fromSec,
        to: toSec,
      });
      setTrack(data);
      setSearched(true);
      setLoading(false);
    } catch (e: any) {
      setLoading(false);
      setSearched(true);
      setTrack(null);
      let msg: string | undefined;
      if (typeof e?.response?.data?.message === "string") {
        msg = e?.response?.data?.message;
      }
      openSnackbar(msg || "Error: Something wrong.");
    }
  };

  const points = track?.points ?? [];
  const path = points.map((p) => ({ lat: p.lat, lng: p.lng }));
  const center =
    path.length > 0
      ? path[Math.floor(path.length / 2)]
      : { lat: 13.7563, lng: 100.5018 };

  return (
    <>
      <h5 className="mt-4 ml-1">ประวัติเส้นทางคนขับ</h5>

      <div className="row mb-3">
        <div className="col-4-lg my-1 ml-3">
          <div className="input-group input-daterange d-flex align-items-center">
            <input
              type="datetime-local"
              className="form-control"
              value={filter.from}
              placeholder="เริ่มต้น"
              onChange={(e) => {
                setFilter((prev) => ({
                  ...prev,
                  from: e.target.value,
                }));
              }}
            />
            <div className="input-group-addon mx-2">ถึง</div>
            <input
              type="datetime-local"
              className="form-control"
              value={filter.to}
              placeholder="สิ้นสุด"
              onChange={(e) => {
                setFilter((prev) => ({
                  ...prev,
                  to: e.target.value,
                }));
              }}
            />
            <button
              onClick={() => searchTrack()}
              disabled={loading}
              className="btn btn-primary btn-sm ml-2"
            >
              ค้นหา
            </button>
          </div>
        </div>
      </div>

      {rangeError && (
        <div className="alert alert-danger ml-3" role="alert">
          {rangeError}
        </div>
      )}

      {track?.truncated && (
        <div className="alert alert-warning ml-3" role="alert">
          แสดงข้อมูลบางส่วน (เกิน 10,000 จุด)
        </div>
      )}

      {searched && !rangeError && points.length === 0 && (
        <div className="ml-3 text-muted">
          ไม่พบข้อมูลเส้นทางในช่วงเวลานี้
        </div>
      )}

      {loadError && (
        <div
          style={{ height: 500, background: "#f0f2f5" }}
          className="align-items-center d-flex justify-content-center text-center"
        >
          Map cannot be loaded right now, sorry.
        </div>
      )}

      {isLoaded && points.length > 0 && (
        <div className="row">
          <div className="col-12 w-100" style={{ height: 500 }}>
            <GoogleMap
              id="driver-track-map"
              mapContainerStyle={{ height: 500, width: "100%" }}
              center={center}
              zoom={15}
            >
              <Polyline
                path={path}
                options={{
                  strokeColor: "#FF0000",
                  strokeOpacity: 1,
                  strokeWeight: 4,
                  clickable: false,
                  draggable: false,
                  editable: false,
                  visible: true,
                  zIndex: 1,
                }}
              />
              <Marker position={path[0]} />
              <Marker position={path[path.length - 1]} />
            </GoogleMap>
          </div>
        </div>
      )}
    </>
  );
};

export default DriverTrackAudit;
